import { useEffect, useMemo, useState } from "react";
import { formatDateRu, formatDateTimeRu } from "../../utils/dateFormat";
import { getHiredEmployees } from "../../services/hrOpsApi";

/**
 * Общие куски для техники и аккаунтов: подписи, форма передачи, история.
 * Чат 4, правка №15 (MR 3), №33. [Claude] 2026-11-02
 */

export const KIND_LABELS = { device: "Техника", sim: "SIM-карта / номер", account: "Аккаунт" };

export const HOLDER_TYPES = [
    ["employee", "Сотруднику"],
    ["department", "Отделу (общий)"],
    ["workplace", "Рабочему месту"],
    ["free", "Свободно"],
];

/** What the «identifier» field means for a category kind. */
export function identifierLabel(kind) {
    if (kind === "sim") return "Номер телефона";
    if (kind === "account") return "Логин / адрес";
    return "Серийный номер / модель";
}

/** Short one-line title of an item: «SIM-карта · +7 927… · инв. 0042». */
export function assetTitle(asset) {
    return [asset?.category, asset?.identifier, asset?.inventory_number ? `инв. ${asset.inventory_number}` : null]
        .filter(Boolean).join(" · ");
}

export function holderText(holder) {
    return holder?.label || "Свободно";
}

/** Validates the move form before sending; returns an error text or null. */
export function moveError(form) {
    if (!form?.type) return "Выберите, кому передать";
    if (form.type === "employee" && !form.employee_id) return "Выберите сотрудника";
    if ((form.type === "department" || form.type === "workplace") && !String(form.label || "").trim()) return "Укажите отдел или рабочее место";
    return null;
}

/** Body for /assets/{id}/move or the «to» of a new item. */
export function moveBody(form) {
    const body = { type: form.type };
    if (form.type === "employee") body.employee_id = Number(form.employee_id);
    if (form.type === "department" || form.type === "workplace") body.label = String(form.label).trim();
    if (String(form.comment || "").trim()) body.comment = String(form.comment).trim();
    return body;
}

const inputCls = "rounded-lg border border-slate-200 px-3 py-2 text-sm";

/** Who gets the item: employee / department / workplace / free. */
export function MoveAssetForm({ initialType = "employee", excludeEmployeeId = null, submitLabel = "Передать", busy = false, onSubmit, onCancel }) {
    const [form, setForm] = useState({ type: initialType, employee_id: "", label: "", comment: "" });
    const [employees, setEmployees] = useState([]);
    const [error, setError] = useState(null);

    useEffect(() => {
        let alive = true;
        Promise.resolve()
            .then(() => getHiredEmployees())
            .then((rows) => { if (alive) setEmployees(Array.isArray(rows) ? rows.filter((e) => !e.date_fired) : []); })
            .catch(() => {});
        return () => { alive = false; };
    }, []);

    const departments = useMemo(
        () => [...new Set(employees.map((e) => e.department).filter(Boolean))].sort((a, b) => a.localeCompare(b, "ru")),
        [employees],
    );
    const choices = employees.filter((e) => String(e.id) !== String(excludeEmployeeId))
        .sort((a, b) => String(a.full_name || "").localeCompare(String(b.full_name || ""), "ru"));

    const submit = (event) => {
        event.preventDefault();
        const problem = moveError(form);
        setError(problem);
        if (!problem) onSubmit?.(moveBody(form));
    };

    return <form onSubmit={submit} className="mt-2 flex flex-wrap items-end gap-2 rounded-xl bg-slate-50 p-3" data-testid="move-asset-form">
        <label className="text-sm"><span className="mb-1 block text-xs font-semibold text-slate-600">Кому</span>
            <select value={form.type} onChange={(e) => setForm((v) => ({ ...v, type: e.target.value }))} className={inputCls} data-testid="move-asset-type">
                {HOLDER_TYPES.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
            </select></label>
        {form.type === "employee" ? <label className="text-sm"><span className="mb-1 block text-xs font-semibold text-slate-600">Сотрудник</span>
            <select value={form.employee_id} onChange={(e) => setForm((v) => ({ ...v, employee_id: e.target.value }))} className={`${inputCls} max-w-[260px]`} data-testid="move-asset-employee">
                <option value="">— выберите —</option>
                {choices.map((e) => <option key={e.id} value={e.id}>{e.full_name}{e.department ? ` · ${e.department}` : ""}</option>)}
            </select></label> : null}
        {form.type === "department" || form.type === "workplace" ? <label className="text-sm"><span className="mb-1 block text-xs font-semibold text-slate-600">{form.type === "department" ? "Отдел" : "Рабочее место / кабинет"}</span>
            <input type="text" list={form.type === "department" ? "asset-departments" : undefined} maxLength={200} value={form.label} onChange={(e) => setForm((v) => ({ ...v, label: e.target.value }))} className={inputCls} data-testid="move-asset-label" />
            <datalist id="asset-departments">{departments.map((d) => <option key={d} value={d} />)}</datalist></label> : null}
        <label className="text-sm"><span className="mb-1 block text-xs font-semibold text-slate-600">Комментарий</span>
            <input type="text" maxLength={1000} value={form.comment} onChange={(e) => setForm((v) => ({ ...v, comment: e.target.value }))} className={inputCls} /></label>
        <button type="submit" disabled={busy} className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-bold text-white hover:bg-slate-700 disabled:opacity-50" data-testid="move-asset-submit">{busy ? "Сохранение…" : submitLabel}</button>
        {onCancel ? <button type="button" onClick={onCancel} className="px-2 py-2 text-sm text-slate-500">Отмена</button> : null}
        {error ? <p className="w-full text-xs font-semibold text-red-600">{error}</p> : null}
    </form>;
}

/** Transfers in time order: when, what happened, from whom → to whom, by whom. */
export function AssetHistoryList({ items }) {
    if (!items?.length) return <p className="text-xs text-slate-400">Истории пока нет</p>;
    return <ol className="space-y-1 text-xs" data-testid="asset-history">
        {items.map((h, i) => <li key={`${h.at}-${i}`} className="flex flex-wrap gap-x-2 text-slate-600">
            <span className="text-slate-400">{h.at ? formatDateTimeRu(h.at) : "—"}</span>
            <span className="font-semibold">{h.reason_label}</span>
            <span>{h.from ? `${holderText(h.from)} → ` : ""}{holderText(h.to)}</span>
            {h.comment ? <span className="text-slate-500">«{h.comment}»</span> : null}
            {h.actor ? <span className="text-slate-400">· {h.actor}</span> : null}
        </li>)}
    </ol>;
}

export const sinceText = (asset) => (asset?.holder_since ? `с ${formatDateRu(asset.holder_since)}` : "");
